/**
 * EquipmentDatabase - Admin Equipment Database Tab Container
 *
 * Provides a tabbed interface for managing equipment databases:
 * Solar, BESS, Generators, Switchgear, Inverters, and EV Chargers.
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper
} from '@mui/material';
import {
    WbSunny as SolarIcon,
    BatteryChargingFull as BESSIcon,
    PowerSettingsNew as GeneratorIcon,
    SettingsInputComponent as SwitchgearIcon,
    PowerInput as InverterIcon,
    EvStation as EVChargerIcon
} from '@mui/icons-material';
import { vortxColors } from '@vortx/shared-ui';

// Equipment database sub-components
import SolarDatabase from './equipment/SolarDatabase';
import BESSDatabase from './equipment/BESSDatabase';
import GeneratorDatabase from './equipment/GeneratorDatabase';
import SwitchgearDatabase from './equipment/SwitchgearDatabase';
import InverterDatabase from './equipment/InverterDatabase';
import EVChargerDatabase from './equipment/EVChargerDatabase';

function EquipmentTabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function EquipmentDatabase() {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ color: vortxColors.white, mb: 2 }}>
                Equipment Database
            </Typography>
            <Typography variant="body2" sx={{ color: vortxColors.stone, mb: 3 }}>
                Manage equipment specifications and pricing for project designs.
            </Typography>

            <Paper sx={{ bgcolor: vortxColors.charcoal, mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    textColor="inherit"
                    indicatorColor="secondary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': {
                            color: vortxColors.stone,
                            minHeight: 56
                        },
                        '& .Mui-selected': { color: vortxColors.gold },
                        '& .MuiTabs-indicator': { bgcolor: vortxColors.gold }
                    }}
                >
                    <Tab
                        icon={<SolarIcon />}
                        iconPosition="start"
                        label="Solar"
                    />
                    <Tab
                        icon={<BESSIcon />}
                        iconPosition="start"
                        label="BESS"
                    />
                    <Tab
                        icon={<GeneratorIcon />}
                        iconPosition="start"
                        label="Generators"
                    />
                    <Tab
                        icon={<SwitchgearIcon />}
                        iconPosition="start"
                        label="Switchgear"
                    />
                    <Tab
                        icon={<InverterIcon />}
                        iconPosition="start"
                        label="Inverters"
                    />
                    <Tab
                        icon={<EVChargerIcon />}
                        iconPosition="start"
                        label="EV Chargers"
                    />
                </Tabs>
            </Paper>

            <EquipmentTabPanel value={tabValue} index={0}>
                <SolarDatabase />
            </EquipmentTabPanel>

            <EquipmentTabPanel value={tabValue} index={1}>
                <BESSDatabase />
            </EquipmentTabPanel>

            <EquipmentTabPanel value={tabValue} index={2}>
                <GeneratorDatabase />
            </EquipmentTabPanel>

            <EquipmentTabPanel value={tabValue} index={3}>
                <SwitchgearDatabase />
            </EquipmentTabPanel>

            <EquipmentTabPanel value={tabValue} index={4}>
                <InverterDatabase />
            </EquipmentTabPanel>

            <EquipmentTabPanel value={tabValue} index={5}>
                <EVChargerDatabase />
            </EquipmentTabPanel>
        </Box>
    );
}

export default EquipmentDatabase;
